import {
  parseOobEvidencePayload,
  type OobEvidencePayload,
} from '../../../artifacts/oob/oob-evidence-payload';

type TraceFileLike = {
  name: string;
  content?: string | null;
};

type OobDetectionToolCallLike = {
  id: string;
  output?: string;
  args?: Record<string, unknown>;
  fileMetadata?: {
    filePaths?: string[];
  };
  metadata?: Record<string, unknown>;
};

const RESULT_PREFIX = 'Result: ';
const TIMING_PREFIX = '\nTiming: ';

const oobStepIdFromToolCallId = (toolCallId: string): string => {
  const separatorIndex = toolCallId.indexOf('_');

  return separatorIndex === -1
    ? toolCallId
    : toolCallId.slice(separatorIndex + 1);
};

const oobArtifactFileNameFromToolCallId = (toolCallId: string): string =>
  `oob_${oobStepIdFromToolCallId(toolCallId)}.json`;

const stringList = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isOobMode = (value: unknown): value is '1' | '2' | '3' =>
  value === '1' || value === '2' || value === '3';

const parseJson = (value: string): unknown => {
  if (!value) {
    return undefined;
  }

  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
};

const extractResultJson = (output: string | undefined): unknown => {
  if (!output) {
    return undefined;
  }

  const resultStart = output.indexOf(RESULT_PREFIX);

  if (resultStart === -1) {
    return parseJson(output.trim());
  }

  const resultEnd = output.indexOf(TIMING_PREFIX, resultStart);
  const resultContent =
    resultEnd === -1
      ? output.slice(resultStart + RESULT_PREFIX.length)
      : output.slice(resultStart + RESULT_PREFIX.length, resultEnd);

  return parseJson(resultContent.trim());
};

const listedFileNames = (toolCall: OobDetectionToolCallLike): string[] => [
  ...(toolCall.fileMetadata?.filePaths ?? []),
  ...stringList(toolCall.metadata?.filesPaths),
  ...stringList(toolCall.metadata?.filePaths),
];

const payloadFromToolOutput = ({
  toolCall,
}: {
  toolCall: OobDetectionToolCallLike;
}): OobEvidencePayload | undefined => {
  const json = extractResultJson(toolCall.output);
  const direct = parseOobEvidencePayload(json);

  if (direct) {
    return direct;
  }

  const mode = toolCall.args?.mode;

  if (!isOobMode(mode) || !isRecord(json)) {
    return undefined;
  }

  return parseOobEvidencePayload({
    ...json,
    schemaVersion: 1,
    stepId: oobStepIdFromToolCallId(toolCall.id),
    mode,
  });
};

export function resolveOobDetectionPayload({
  toolCall,
  files,
}: {
  toolCall: OobDetectionToolCallLike;
  files: TraceFileLike[];
}): OobEvidencePayload | undefined {
  const expectedName = oobArtifactFileNameFromToolCallId(toolCall.id);
  const names = new Set([expectedName, ...listedFileNames(toolCall)]);
  const stepId = oobStepIdFromToolCallId(toolCall.id);
  const namedFile = files.find((file) => names.has(file.name));
  const fromNamedFile = namedFile
    ? parseOobEvidencePayload(namedFile.content)
    : undefined;

  if (fromNamedFile) {
    return fromNamedFile;
  }

  const matchingFile = files.find((file) => {
    if (!file.name.startsWith('oob_') || !file.name.endsWith('.json')) {
      return false;
    }

    return parseOobEvidencePayload(file.content)?.stepId === stepId;
  });

  if (matchingFile) {
    return parseOobEvidencePayload(matchingFile.content);
  }

  return payloadFromToolOutput({ toolCall });
}
